import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { TOUR_STEPS, type TourStep } from './tourSteps';
import { editorStore, useEditorStore, type EditorState } from '../editorStore';
import './tour.css';

interface SpotRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export default function GuidedTour({ steps = TOUR_STEPS }: { steps?: TourStep[] }) {
  const [open, setOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [showNice, setShowNice] = useState(false);
  const [spotRect, setSpotRect] = useState<SpotRect | null>(null);
  const [tooltipStyle, setTooltipStyle] = useState<CSSProperties>({ top: 0, left: 0 });
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const primaryButtonRef = useRef<HTMLButtonElement | null>(null);
  const stepStartStateRef = useRef<EditorState | null>(null);
  const editorState = useEditorStore();

  const closeTour = useCallback(() => {
    setOpen(false);
    setShowNice(false);
    try {
      window.localStorage.setItem('sarnia_tour_done', '1');
    } catch {
      // Local storage can be unavailable in private browsing; the tour still closes.
    }
  }, []);

  const beginTour = useCallback(() => {
    setShowNice(false);
    setStepIndex(0);
    setOpen(true);
  }, []);

  const onBack = useCallback(() => {
    setShowNice(false);
    setStepIndex((index) => Math.max(0, index - 1));
  }, []);

  const onNext = useCallback(() => {
    setShowNice(false);
    if (stepIndex >= steps.length - 1) {
      closeTour();
    } else {
      setStepIndex((index) => Math.min(index + 1, steps.length - 1));
    }
  }, [closeTour, stepIndex, steps.length]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    if (params.get('tour') === '1') {
      params.delete('tour');
      const query = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
      beginTour();
    }

    const onStartTour = () => beginTour();
    window.addEventListener('sarnia:start-tour', onStartTour);
    return () => window.removeEventListener('sarnia:start-tour', onStartTour);
  }, [beginTour]);

  useEffect(() => {
    if (!open) return;
    const step = steps[stepIndex];
    if (!step?.selector) return;

    const target = document.querySelector<HTMLElement>(step.selector);
    if (!target) {
      setSpotRect(null);
      setTooltipStyle({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
    }
  }, [open, stepIndex]);

  useEffect(() => {
    if (!open) return;
    stepStartStateRef.current = editorStore.getState();
  }, [open, stepIndex]);

  useEffect(() => {
    if (!open) return;

    const handleViewportChange = () => {
      const step = steps[stepIndex];
      if (!step) return;

      if (!step.selector) {
        setSpotRect(null);
        setTooltipStyle({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
        return;
      }

      const target = document.querySelector<HTMLElement>(step.selector);
      if (!target) {
        setSpotRect(null);
        return;
      }

      const rect = target.getBoundingClientRect();
      setSpotRect({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });

      const tooltip = tooltipRef.current;
      if (!tooltip) {
        setTooltipStyle({ top: rect.top, left: rect.left });
        return;
      }

      const tipRect = tooltip.getBoundingClientRect();
      const gap = 12;
      const margin = 12;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let top = 0;
      let left = 0;

      switch (step.placement) {
        case 'top':
          top = rect.top - tipRect.height - gap;
          left = rect.left + rect.width / 2 - tipRect.width / 2;
          break;
        case 'bottom':
          top = rect.bottom + gap;
          left = rect.left + rect.width / 2 - tipRect.width / 2;
          break;
        case 'left':
          top = rect.top + rect.height / 2 - tipRect.height / 2;
          left = rect.left - tipRect.width - gap;
          break;
        case 'right':
          top = rect.top + rect.height / 2 - tipRect.height / 2;
          left = rect.right + gap;
          break;
        case 'center':
        default:
          top = rect.top + rect.height / 2 - tipRect.height / 2;
          left = rect.left + rect.width / 2 - tipRect.width / 2;
          break;
      }

      top = Math.max(margin, Math.min(top, vh - tipRect.height - margin));
      left = Math.max(margin, Math.min(left, vw - tipRect.width - margin));
      setTooltipStyle({ top, left });
    };

    handleViewportChange();
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => handleViewportChange()) : null;
    if (observer) observer.observe(document.body);

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
      observer?.disconnect();
    };
  }, [open, stepIndex]);

  useEffect(() => {
    if (!open) return;
    const step = steps[stepIndex];
    if (!step?.advanceWhen) return;

    const startState = stepStartStateRef.current;
    if (!startState) return;

    if (step.advanceWhen(editorState, startState)) {
      setShowNice(true);
      const timer = window.setTimeout(() => {
        setShowNice(false);
        setStepIndex((index) => Math.min(index + 1, steps.length - 1));
      }, 900);

      return () => window.clearTimeout(timer);
    }
  }, [editorState, open, stepIndex]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeTour();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [closeTour, open]);

  useEffect(() => {
    if (!open) return;

    if (typeof window.requestAnimationFrame !== 'function') {
      primaryButtonRef.current?.focus();
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      primaryButtonRef.current?.focus();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [open, stepIndex]);

  if (!open) return null;

  const step = steps[stepIndex];
  if (!step) return null;
  const titleId = `tour-title-${step.id}`;
  const bodyId = `tour-body-${step.id}`;
  const stepNumber = stepIndex + 1;
  const totalSteps = steps.length;

  return (
    <div className={`tour-overlay${showNice ? ' tour-overlay--nice' : ''}`}>
      {spotRect ? (
        <div
          className="tour-spot"
          style={{ top: spotRect.top, left: spotRect.left, width: spotRect.width, height: spotRect.height }}
        />
      ) : null}

      <div
        className="tour-tooltip"
        role="dialog"
        aria-modal="false"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        ref={tooltipRef}
        style={tooltipStyle}
      >
        {showNice ? <div className="tour-nice" aria-live="polite">Nice!</div> : null}
        <p className="tour-step-count">Step {stepNumber} of {totalSteps}</p>
        <h2 className="tour-title" id={titleId}>{step.title}</h2>
        <p className="tour-body" id={bodyId}>{step.body}</p>

        <div className="tour-progress" aria-hidden="true">
          {steps.map((tourStep, index) => (
            <span
              key={tourStep.id}
              className={`tour-dot${index === stepIndex ? ' tour-dot--active' : ''}${index < stepIndex ? ' tour-dot--done' : ''}`}
            />
          ))}
        </div>

        <div className="tour-actions">
          <button type="button" className="tour-skip" onClick={closeTour}>Skip tour</button>
          <span className="tour-actions__spacer" />
          {stepIndex > 0 ? (
            <button type="button" className="tour-secondary" onClick={onBack}>Back</button>
          ) : null}
          <button type="button" className="tour-primary" ref={primaryButtonRef} onClick={onNext}>
            {stepIndex === totalSteps - 1 ? 'Done' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}
